import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icon in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Component to update map center when coordinates change
const MapUpdater = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, 15);
    }
  }, [center, map]);
  return null;
};

const LocationMap = ({ address, city, state, zipCode, locationName }) => {
  const [coordinates, setCoordinates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const geocodeAddress = async () => {
      // Build address string for geocoding
      const addressParts = [address, city, state, zipCode].filter(Boolean);
      if (addressParts.length === 0) {
        setError('No address provided');
        setLoading(false);
        return;
      }

      const fullAddress = addressParts.join(', ');
      
      try {
        // Use Nominatim (OpenStreetMap's geocoding service)
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(fullAddress)}&limit=1`,
          {
            headers: {
              'User-Agent': 'AVFactoryCRM/1.0'
            }
          }
        );
        
        const data = await response.json();
        
        if (data && data.length > 0) {
          const { lat, lon } = data[0];
          setCoordinates([parseFloat(lat), parseFloat(lon)]);
          setError(null);
        } else {
          // Try with just city and state if full address fails
          const fallbackAddress = [city, state].filter(Boolean).join(', ');
          if (fallbackAddress) {
            const fallbackResponse = await fetch(
              `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(fallbackAddress)}&limit=1`,
              {
                headers: {
                  'User-Agent': 'AVFactoryCRM/1.0'
                }
              }
            );
            const fallbackData = await fallbackResponse.json();
            if (fallbackData && fallbackData.length > 0) {
              const { lat, lon } = fallbackData[0];
              setCoordinates([parseFloat(lat), parseFloat(lon)]);
              setError(null);
            } else {
              setError('Location not found');
            }
          } else {
            setError('Location not found');
          }
        }
      } catch (err) {
        console.error('Geocoding error:', err);
        setError('Failed to load map');
      } finally {
        setLoading(false);
      }
    };

    geocodeAddress();
  }, [address, city, state, zipCode]);

  if (loading) {
    return (
      <div className="h-[300px] rounded-lg bg-background/50 flex items-center justify-center border border-border/50">
        <div className="flex flex-col items-center gap-2">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
          <p className="text-sm text-muted-foreground">Loading map...</p>
        </div>
      </div>
    );
  }

  if (error || !coordinates) {
    return (
      <div className="h-[300px] rounded-lg bg-background/50 flex items-center justify-center border border-border/50">
        <div className="text-center">
          <p className="text-muted-foreground">{error || 'Unable to display map'}</p>
          <p className="text-sm text-muted-foreground mt-1">
            Add a complete address to see the map
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[300px] rounded-lg overflow-hidden border border-border/50" data-testid="location-map">
      <MapContainer
        center={coordinates}
        zoom={15}
        scrollWheelZoom={true}
        style={{ height: '100%', width: '100%' }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <Marker position={coordinates}>
          <Popup>
            <div className="text-sm">
              <strong>{locationName}</strong>
              {address && <p>{address}</p>}
              {(city || state) && (
                <p>{[city, state, zipCode].filter(Boolean).join(', ')}</p>
              )}
            </div>
          </Popup>
        </Marker>
        <MapUpdater center={coordinates} />
      </MapContainer>
    </div>
  );
};

export default LocationMap;
